import React from 'react';
import './StudentOverview.css';
import { Box, Typography, Button } from '@mui/material';

import dayjs from 'dayjs';

const formatText = text => {
  if (!text) return '';

  // Replace underscores with spaces
  const withSpaces = text.replace(/_/g, ' ');

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Function to calculate age from date of birth
const calculateAge = dob => {
  if (!dob) return null;
  const birthDate = dayjs(dob);
  const today = dayjs();
  return today.diff(birthDate, 'year');
};

const LevelToTeachColor = {
  university: { color: '#14B334', bgcolor: '#F2FFF5', value: 'University' },
  high_school: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'High School' },
  middle_school: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Middle School' },
  elementary: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Elementary School' },
};

const StudentOverview = ({ studentDetails }) => {
  const studentAge = calculateAge(studentDetails?.dob);
  const isUnder18 = studentAge !== null && studentAge < 18;
  const hasParentDocument = studentDetails?.parents_document_url;

  return (
    <Box className="flex flex-col items-start gap-[19px] bg-[#FFF] rounded-[8px]">
      <Box className="flex flex-col gap-4 w-[100%]">
        <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="session-history-key-text">Total Hours</Typography>
            <Typography className="session-history-value-text">
              {studentDetails?.total_hours ? studentDetails?.total_hours : '0'} hours
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="session-history-key-text">Total Sessions</Typography>
            <Typography className="session-history-value-text">
              {studentDetails?.total_sessions ? studentDetails?.total_sessions : '0'}
            </Typography>
          </Box>
        </Box>
        <Typography className="personal-information-text">Personal Information</Typography>
        <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">First Name</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.first_name ? formatText(studentDetails?.first_name) : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Last Name</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.last_name ? formatText(studentDetails?.last_name) : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Email</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.email ? studentDetails?.email : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Phone Number</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.phone_no ? studentDetails?.phone_no : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Date of Birth</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.dob ? dayjs(studentDetails?.dob).format('DD MMM YYYY') : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Gender</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.gender ? formatText(studentDetails?.gender) : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">School Name</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.school_name ? formatText(studentDetails?.school_name) : 'NA'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">School Level</Typography>
            <Typography className="personal-information-value-text">
              {studentDetails?.school_level
                ? LevelToTeachColor[studentDetails?.school_level]?.value
                : 'NA'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[30%]">
            <Typography className="personal-information-key-text">Current Location</Typography>
            <Typography className="personal-information-value-text">
              {(() => {
                const city = studentDetails?.city;
                const country = studentDetails?.country;
                if (city && country && city !== country) {
                  return formatText(`${city}, ${country}`);
                }
                if (city) {
                  return formatText(city);
                }
                if (country) {
                  return formatText(country);
                }
                return '---';
              })()}
            </Typography>
          </Box>
        </Box>

        {/* Parent Document Section - Only show if student is under 18 and has parent document */}
        {isUnder18 && hasParentDocument && (
          <>
            <Typography className="personal-information-text">Parent Document</Typography>
            <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
              <Box className="flex flex-col gap-2 w-[100%]">
                <Typography className="personal-information-key-text">
                  Parent Consent Document
                </Typography>
                <Box className="flex items-center gap-3">
                  <Typography className="personal-information-value-text">
                    Parent consent document is available
                  </Typography>
                  <a
                    href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(
                      studentDetails.parents_document_url
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="parent-document-view-button"
                  >
                    View Document
                  </a>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default StudentOverview;
